<template>
  <div class="container-fluid">
    <div class="card">
      <div class="card-header bg-light">
        <h5 class="card-title">Reporte de Kardex</h5>
        
        <!-- Filtros -->
        <div class="row mb-3">
          <!-- Gestión -->
          <div class="col-md-2">
            <label class="form-label">Gestión</label>
            <select v-model="filters.gestion" class="form-select">
              <option value="">Todas</option>
              <option v-for="year in filterOptions.gestiones" :key="year" :value="year">
                {{ year }}
              </option>
            </select>
          </div>
          
          <!-- Rango de fechas -->
          <div class="col-md-3">
            <label class="form-label">Rango de fechas</label>
            <div class="input-group">
              <input v-model="filters.fecha_inicio" type="date" class="form-control">
              <span class="input-group-text">a</span>
              <input v-model="filters.fecha_fin" type="date" class="form-control">
            </div>
          </div>
          
          <!-- Partida -->
          <div class="col-md-2">
            <label class="form-label">Partida</label>
            <select v-model="filters.partida_id" class="form-select">
              <option value="">Todas</option>
              <option v-for="partida in filterOptions.partidas" :key="partida.codigo" :value="partida.codigo">
                {{ partida.codigo }} - {{ partida.nombre }}
              </option>
            </select>
          </div>
          
          <!-- Unidad Solicitante -->
          <div class="col-md-2">
            <label class="form-label">Unidad Solicitante</label>
            <select v-model="filters.unidad_id" class="form-select">
              <option value="">Todas</option>
              <option v-for="unidad in filterOptions.unidades" :key="unidad.id" :value="unidad.id">
                {{ unidad.nombre_unidad }}
              </option>
            </select>
          </div>
          
          <!-- Categoría -->
          <div class="col-md-2">
            <label class="form-label">Categoría</label>
            <select v-model="filters.categoria_id" class="form-select">
              <option value="">Todas</option>
              <option v-for="cat in filterOptions.categorias" :key="cat.id" :value="cat.id">
                {{ cat.nombre_categoria }}
              </option>
            </select>
          </div>
          
          <!-- Botones de acción -->
          <div class="col-md-12 mt-3">
            <div class="d-flex justify-content-end">
              <button @click="applyFilters" class="btn btn-primary me-2">
                <i class="fas fa-search me-1"></i> Buscar
              </button>
              <button @click="resetFilters" class="btn btn-secondary me-2">
                <i class="fas fa-undo me-1"></i> Limpiar
              </button>
              <button @click="exportToPDF" class="btn btn-danger">
                <i class="fas fa-file-pdf me-1"></i> Exportar PDF
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div class="card-body">
        <!-- Loading -->
        <div v-if="loading" class="text-center">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
        </div>
        
        <!-- Resultados -->
        <div v-else>
          <div v-if="kardexData.length === 0" class="alert alert-info">
            No se encontraron resultados con los filtros aplicados.
          </div>
          
          <div v-else v-for="(itemData, index) in kardexData" :key="itemData.item.id" class="mb-4">
            <h5>{{ index + 1 }}. {{ itemData.item.nombre_item }} ({{ itemData.item.unidad_medida }})</h5>
            
            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                  <tr>
                    <th>#</th>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Cantidad</th>
                    <th>Costo Unitario</th>
                    <th>Valor Total</th>
                    <th>Saldo</th>
                    <th>Referencia</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Saldo inicial -->
                  <tr>
                    <td>-</td>
                    <td>-</td>
                    <td><span class="badge bg-secondary">SALDO INICIAL</span></td>
                    <td>{{ itemData.saldo_inicial }}</td>
                    <td>-</td>
                    <td>-</td>
                    <td>{{ itemData.saldo_inicial }}</td>
                    <td>-</td>
                  </tr>
                  
                  <!-- Movimientos -->
                  <tr v-for="(mov, movIndex) in itemData.movimientos" :key="mov.id">
                    <td>{{ movIndex + 1 }}</td>
                    <td>{{ formatDate(mov.fecha_movimiento) }}</td>
                    <td>
                      <span :class="{
                        'badge bg-success': mov.tipo_movimiento === 'entrada',
                        'badge bg-danger': mov.tipo_movimiento === 'salida'
                      }">
                        {{ mov.tipo_movimiento.toUpperCase() }}
                      </span>
                    </td>
                    <td>{{ mov.cantidad }}</td>
                    <td>{{ formatCurrency(mov.costo_unitario) }}</td>
                    <td>{{ formatCurrency(mov.cantidad * mov.costo_unitario) }}</td>
                    <td>{{ calculateSaldo(itemData, movIndex) }}</td>
                    <td>{{ mov.referencia }}</td>
                  </tr>
                  
                  <!-- Totales -->
                  <tr class="table-info">
                    <td colspan="3" class="text-end fw-bold">TOTALES</td>
                    <td class="fw-bold">{{ itemData.total_entradas + itemData.total_salidas }}</td>
                    <td>-</td>
                    <td>-</td>
                    <td class="fw-bold">{{ itemData.saldo_final }}</td>
                    <td>-</td>
                  </tr>
                  <tr class="table-success">
                    <td colspan="3" class="text-end fw-bold">ENTRADAS</td>
                    <td class="fw-bold">{{ itemData.total_entradas }}</td>
                    <td colspan="4"></td>
                  </tr>
                  <tr class="table-warning">
                    <td colspan="3" class="text-end fw-bold">SALIDAS</td>
                    <td class="fw-bold">{{ itemData.total_salidas }}</td>
                    <td colspan="4"></td>
                  </tr>
                  <tr class="table-primary">
                    <td colspan="3" class="text-end fw-bold">SALDO FINAL</td>
                    <td class="fw-bold">{{ itemData.saldo_final }}</td>
                    <td colspan="4"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'

const loading = ref(false)
const kardexData = ref([])
const filterOptions = ref({
  categorias: [],
  unidades: [],
  partidas: [],
  gestiones: []
})

const filters = ref({
  gestion: '',
  fecha_inicio: '',
  fecha_fin: '',
  partida_id: '',
  unidad_id: '',
  categoria_id: ''
})

// Obtener opciones de filtro
const fetchFilterOptions = async () => {
  try {
    const response = await axios.get('/reports/filter-options')
    filterOptions.value = response.data.data
  } catch (error) {
    console.error('Error fetching filter options:', error)
  }
}

// Aplicar filtros
const applyFilters = async () => {
  try {
    loading.value = true
    const params = { ...filters.value }
    
    const response = await axios.get('/reports/kardex', { params })
    kardexData.value = response.data.data
  } catch (error) {
    console.error('Error fetching kardex data:', error)
    alert('Error al obtener los datos del kardex')
  } finally {
    loading.value = false
  }
}

// Resetear filtros
const resetFilters = () => {
  filters.value = {
    gestion: '',
    fecha_inicio: '',
    fecha_fin: '',
    partida_id: '',
    unidad_id: '',
    categoria_id: ''
  }
  kardexData.value = []
}

// Exportar a PDF
const exportToPDF = async () => {
    try {
        const params = { ...filters.value };

        const response = await axios.get('/reports/kardex/export', {
            params,
            responseType: 'blob'
        });
        
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `reporte-kardex-${new Date().toISOString().split('T')[0]}.pdf`;
        link.click();
        URL.revokeObjectURL(link.href);
    } catch (error) {
        console.error('Error exporting kardex to PDF:', error);
        alert('Error al generar el reporte de kardex');
    }
}

// Calcular saldo acumulado
const calculateSaldo = (itemData, movIndex) => {
  let saldo = itemData.saldo_inicial
  for (let i = 0; i <= movIndex; i++) {
    const mov = itemData.movimientos[i]
    if (mov.tipo_movimiento === 'entrada') {
      saldo += mov.cantidad
    } else {
      saldo -= mov.cantidad
    }
  }
  return saldo
}

// Formatear fecha
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('es-ES')
}

// Formatear moneda
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-BO', {
    style: 'currency',
    currency: 'BOB',
    minimumFractionDigits: 2
  }).format(amount)
}

onMounted(() => {
  fetchFilterOptions()
})
</script>

<style scoped>
.table-responsive {
  max-height: 70vh;
  overflow-y: auto;
}
</style>